import React, {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import {
  DEFAULT_PERIOD_SCALE,
  DEFAULT_DISPLAY_UNIT
} from '~/modules/resourcing/common/enums';
import { useGetKeyValueSettings } from '~/modules/common/hooks';
import DisplayUnitContextProvider from '~/modules/resourcing/common/hooks/useDisplayUnitContext';
import useResourcingStateCallbacks from '~/modules/resourcing/hooks/useResourcingStateCallbacks';
import { useGetChartDates as useGetChartDatesFromAnchorDate } from '~/modules/resourcing/common/hooks/useGetChartDates';
import { ResourcePlanGroupBy } from '~/types';
import { useChartDisplayPeriods } from '../common/chart/hooks';

export const ResourceAllocationToolbarContext = createContext();

const RESOURCINGPAGE_CHART_SETTINGS = 'resourcingPage_chart_settings';

export const ResourceAllocationToolbarContextProvider = ({
  children,
  width
}) => {
  const {
    isLoading: isResourcingPageChartSettingsLoading,
    keyValueSettings: chartSettings
  } = useGetKeyValueSettings(RESOURCINGPAGE_CHART_SETTINGS, false);

  const [scale, setScale] = useState(DEFAULT_PERIOD_SCALE);

  const [displayUnit, setDisplayUnit] = useState(DEFAULT_DISPLAY_UNIT);

  const { onDisplayUnitChange, onScaleChange } = useResourcingStateCallbacks({
    scale,
    displayUnit,
    setDisplayUnit,
    setScale,
    key: RESOURCINGPAGE_CHART_SETTINGS
  });
  const [resourcePlanGroup, setResourcePlanGroup] = useState(
    ResourcePlanGroupBy.Project
  );

  const today = DateTime.utc();

  const {
    chartDates,
    onPrevious,
    onNext,
    dateRange: fixedRangeDateRange,
    setFixedRangeScale,
    setDate
  } = useGetChartDatesFromAnchorDate({
    anchorDate: today,
    scale,
    width,
    sessionStorageKey: 'resource-request-chart-start-date'
  });

  const chartDisplayPeriods = useChartDisplayPeriods({
    chartDisplayDateRange: fixedRangeDateRange,
    scale
  });

  const onScaleChangeHandler = useCallback(
    newValue => {
      onScaleChange(newValue);
      setFixedRangeScale(newValue);
    },
    [onScaleChange, setFixedRangeScale]
  );

  const value = useMemo(
    () => ({
      scale,
      setScale: onScaleChangeHandler,
      displayUnit,
      setDisplayUnit: onDisplayUnitChange,
      resourcePlanGroup,
      setResourcePlanGroup,
      dateRange: fixedRangeDateRange,
      chartDates,
      onPreviousClick: onPrevious,
      onNextClick: onNext,
      chartDisplayPeriods,
      isResourcingPageChartSettingsLoading,
      setDate
    }),
    [
      scale,
      onScaleChangeHandler,
      displayUnit,
      onDisplayUnitChange,
      resourcePlanGroup,
      setResourcePlanGroup,
      chartDates,
      onPrevious,
      onNext,
      fixedRangeDateRange,
      chartDisplayPeriods,
      isResourcingPageChartSettingsLoading,
      setDate
    ]
  );

  useEffect(() => {
    if (!isResourcingPageChartSettingsLoading) {
      setScale(chartSettings?.scale || DEFAULT_PERIOD_SCALE);
      setDisplayUnit(chartSettings?.displayUnit || DEFAULT_DISPLAY_UNIT);
    }
  }, [chartSettings, isResourcingPageChartSettingsLoading]);

  return (
    <DisplayUnitContextProvider
      defaultDisplayUnit={displayUnit}
      onDisplayUnitChange={onDisplayUnitChange}
    >
      <ResourceAllocationToolbarContext.Provider value={value}>
        {children}
      </ResourceAllocationToolbarContext.Provider>
    </DisplayUnitContextProvider>
  );
};
ResourceAllocationToolbarContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  width: PropTypes.number
};

export const useResourceAllocationToolbarStateContext = () => {
  const {
    dateRange,
    onPreviousClick,
    onNextClick,
    displayUnit,
    setDisplayUnit,
    scale,
    setScale,
    resourcePlanGroup,
    setResourcePlanGroup,
    chartDates,
    chartDisplayPeriods,
    isResourcingPageChartSettingsLoading,
    setDate
  } = useContext(ResourceAllocationToolbarContext) || {};

  return {
    dateRange,
    onPreviousClick,
    onNextClick,
    displayUnit,
    setDisplayUnit,
    scale,
    setScale,
    resourcePlanGroup,
    setResourcePlanGroup,
    chartDates,
    chartDisplayPeriods,
    isResourcingPageChartSettingsLoading,
    setDate
  };
};
export default useResourceAllocationToolbarStateContext;
